import { createFileRoute, Navigate, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Shield, CheckCircle2, XCircle, Eye, Sparkles, Settings as SettingsIcon, Users,
  ExternalLink, Search, ChevronLeft, ChevronRight, BadgeCheck, Crown, Award, Inbox,
  Megaphone, Image as ImageIcon, Trash2, EyeOff, FileText, Video as VideoIcon,
  LayoutDashboard, TrendingUp, MessageSquare, Clock, AlertTriangle, ArrowRight,
  CreditCard, Mail, Activity, UserCog, History, Ban, RotateCcw, BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  adminListVerifications, adminGetVerificationDocs, adminReviewVerification,
  adminGetPromoSettings, adminUpdatePromoSettings, adminMe,
  adminListChefs, adminToggleChefFlag, adminOverview,
  adminListListings, adminToggleListing, adminDeleteListing,
  adminListMedia, adminDeleteMedia,
  adminCeoExtras,
  adminListUsers, adminSuspendUser, adminReactivateUser,
  adminUpdateUserRoles, adminDeleteUser, adminListAuditLog,
  adminSetPremiumTier,
} from "@/lib/admin.functions";
import { getAdminKpis, getAdminTops } from "@/lib/analytics.functions";

export const Route = createFileRoute("/_authenticated/dashboard/admin")({
  head: () => ({ meta: [{ title: "Admin — ChefConnect" }] }),
  beforeLoad: async ({ context }) => {
    // Gate route-level: niente flash di UI admin a non-admin.
    // ssr:false sul layout _authenticated → questo gira solo client-side.
    const user = (context as { user?: { id: string } | null }).user;
    if (!user) throw redirect({ to: "/auth", search: { mode: "signin" } });
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
    if (!data) throw redirect({ to: "/dashboard" });
  },
  component: AdminPage,
});

function AdminPage() {
  const meFn = useServerFn(adminMe);
  const meQ = useQuery({ queryKey: ["admin-me"], queryFn: () => meFn() });
  const [tab, setTab] = useState<string>("ceo");

  if (meQ.isLoading) return <p className="p-10 text-center text-sm text-muted-foreground">Caricamento…</p>;
  if (!meQ.data?.isAdmin) return <Navigate to="/dashboard" />;

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard</p>
          <h1 className="mt-1 font-display text-4xl inline-flex items-center gap-2">
            <Shield className="h-7 w-7 text-gold" /> Pannello Admin
          </h1>
        </div>
        <AdminModeBadge />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Stai gestendo ChefConnect come amministratore.
      </p>
      <SystemStatusBanner />

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList>
          <TabsTrigger value="ceo"><Crown className="mr-1.5 h-4 w-4 text-gold" /> CEO</TabsTrigger>
          <TabsTrigger value="business"><BarChart3 className="mr-1.5 h-4 w-4 text-gold" /> Business</TabsTrigger>
          <TabsTrigger value="overview"><LayoutDashboard className="mr-1.5 h-4 w-4" /> Overview</TabsTrigger>
          <TabsTrigger value="verif"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Verifiche</TabsTrigger>
          <TabsTrigger value="chefs"><Users className="mr-1.5 h-4 w-4" /> Chef</TabsTrigger>
          <TabsTrigger value="listings"><Megaphone className="mr-1.5 h-4 w-4" /> Annunci</TabsTrigger>
          <TabsTrigger value="media"><ImageIcon className="mr-1.5 h-4 w-4" /> Media</TabsTrigger>
          <TabsTrigger value="promo"><Sparkles className="mr-1.5 h-4 w-4" /> Promo Lancio</TabsTrigger>
          <TabsTrigger value="users"><UserCog className="mr-1.5 h-4 w-4" /> Utenti</TabsTrigger>
          <TabsTrigger value="audit"><History className="mr-1.5 h-4 w-4" /> Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="ceo" className="mt-6"><CeoTab onNavigate={setTab} /></TabsContent>
        <TabsContent value="business" className="mt-6"><BusinessTab /></TabsContent>
        <TabsContent value="overview" className="mt-6"><OverviewTab onNavigate={setTab} /></TabsContent>
        <TabsContent value="verif" className="mt-6"><VerificationsTab /></TabsContent>
        <TabsContent value="chefs" className="mt-6"><ChefsTab /></TabsContent>
        <TabsContent value="listings" className="mt-6"><ListingsTab /></TabsContent>
        <TabsContent value="media" className="mt-6"><MediaTab /></TabsContent>
        <TabsContent value="promo" className="mt-6"><PromoTab /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// Sprint 25.2 — Badge "Modalità Amministratore" sempre visibile sulle pagine admin.
function AdminModeBadge() {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-lg border border-gold/40 bg-gold-muted/20 px-3 py-1.5 text-xs font-medium"
      title="Modalità Amministratore"
    >
      <Shield className="h-4 w-4 text-gold" />
      <span>🛡️ Modalità Amministratore</span>
    </div>
  );
}

// Sprint 25.2 — Indicatore stato sistema. Usa solo segnali reali esposti da adminCeoExtras.
function SystemStatusBanner() {
  const extrasFn = useServerFn(adminCeoExtras);
  const q = useQuery({ queryKey: ["admin-ceo-extras"], queryFn: () => extrasFn(), staleTime: 30_000 });
  const ex = q.data;
  if (q.isLoading || !ex) return null;
  const issues: string[] = [];
  if (ex.email_health === "red") issues.push("email");
  if (!ex.stripe_configured) issues.push("Stripe");
  if (issues.length === 0) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1 text-xs text-emerald-700 dark:text-emerald-300">
        🟢 Tutti i servizi operativi
      </div>
    );
  }
  return (
    <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-800 dark:text-amber-200">
      🟠 Attenzione richiesta · {issues.length} criticità ({issues.join(", ")})
    </div>
  );
}

// =========================================================
// Business Analytics — KPI reali dal sistema analytics_events.
// Nessun dato inventato: se non c'è ancora traffico, si vede 0.
// =========================================================
function BusinessTab() {
  const [days, setDays] = useState<number>(30);
  const kpisFn = useServerFn(getAdminKpis);
  const topsFn = useServerFn(getAdminTops);
  const kpisQ = useQuery({ queryKey: ["admin-kpis", days], queryFn: () => kpisFn({ data: { days } }) });
  const topsQ = useQuery({ queryKey: ["admin-tops", days], queryFn: () => topsFn({ data: { days, limit: 10 } }) });

  const k = kpisQ.data;
  const t = topsQ.data;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Business Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Tutti i dati derivano da eventi reali registrati sulla piattaforma. Nessuna stima, nessuna simulazione.
          </p>
        </div>
        <div className="flex gap-1 rounded-md border border-border p-0.5 text-xs">
          {[7, 30, 90].map((d) => (
            <button key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded ${days === d ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
            >{d} giorni</button>
          ))}
        </div>
      </div>

      <Block title="Traffico" desc="Visitatori unici (hash giornaliero anonimo, no cookie).">
        <Kpi label="Visitatori oggi" value={k?.visitors_today} desc="Persone uniche che hanno generato almeno un evento oggi." loading={kpisQ.isLoading} />
        <Kpi label={`Visitatori (${days}g)`} value={k?.visitors_period} desc="Visitatori unici nel periodo selezionato." loading={kpisQ.isLoading} />
        <Kpi label="Visualizzazioni profilo" value={k?.profile_views} desc="Apertura della pagina di uno chef." loading={kpisQ.isLoading} />
        <Kpi label="Impression in ricerca" value={k?.search_impressions} desc="Chef mostrati nei risultati di ricerca." loading={kpisQ.isLoading} />
      </Block>

      <Block title="Business" desc="Crescita della piattaforma nel periodo.">
        <Kpi label="Nuovi utenti" value={k?.new_users} desc="Registrazioni nuove (client + chef)." loading={kpisQ.isLoading} />
        <Kpi label="Nuovi chef" value={k?.new_chefs} desc="Profili chef creati nel periodo." loading={kpisQ.isLoading} />
        <Kpi label="Richieste inviate" value={k?.contact_requests} desc="Form di contatto compilati dai clienti." loading={kpisQ.isLoading} />
        <Kpi label="Click WhatsApp" value={k?.whatsapp_clicks} desc="Tap sul bottone WhatsApp sui profili." loading={kpisQ.isLoading} />
      </Block>

      <Block title="Premium" desc="Stato attuale del parco chef e dinamica del periodo.">
        <Kpi label="Verificati" value={k?.chefs_verified} desc="Chef con badge verifica attivo." loading={kpisQ.isLoading} />
        <Kpi label="Founder" value={k?.chefs_founders} desc="Badge permanente dei primi 100 chef." loading={kpisQ.isLoading} />
        <Kpi label="Premium Silver" value={k?.chefs_silver} desc="Abbonamento Silver attivo." loading={kpisQ.isLoading} />
        <Kpi label="Premium Gold" value={k?.chefs_gold} desc="Abbonamento Gold attivo." loading={kpisQ.isLoading} />
        <Kpi label="Upgrade Premium" value={k?.premium_upgrades} desc="Prime attivazioni nel periodo." loading={kpisQ.isLoading} />
        <Kpi label="Downgrade Premium" value={k?.premium_downgrades} desc="Cancellazioni / downgrade nel periodo." loading={kpisQ.isLoading} />
      </Block>

      <Block title="Conversioni" desc="Calcolate solo quando i dati sono sufficienti.">
        <Kpi
          label="CTR ricerca → profilo"
          value={k && k.search_impressions > 0 ? `${Math.round((k.profile_views / k.search_impressions) * 100)}%` : null}
          desc="Quante impression diventano visite reali."
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Conv. profilo → richiesta"
          value={k && k.profile_views > 0 ? `${Math.round((k.contact_requests / k.profile_views) * 100)}%` : null}
          desc="Visite che generano un contatto."
          loading={kpisQ.isLoading}
        />
        <Kpi
          label="Affidabilità email"
          value={k && k.emails_sent + k.emails_failed > 0
            ? `${Math.round((k.emails_sent / (k.emails_sent + k.emails_failed)) * 100)}%`
            : null}
          desc="Email consegnate vs totali (incl. fallite)."
          loading={kpisQ.isLoading}
        />
        <Kpi label="Email fallite" value={k?.emails_failed} desc="Bounce, complaint, errori provider." loading={kpisQ.isLoading} />
      </Block>

      <div className="grid gap-4 lg:grid-cols-2">
        <TopList title="Top città (chef registrati)" rows={t?.cities ?? []} loading={topsQ.isLoading} />
        <TopList title="Top categorie cucina" rows={t?.cuisines ?? []} loading={topsQ.isLoading} />
        <TopList title={`Chef più visualizzati (${days}g)`}
          rows={(t?.most_viewed ?? []).map((x: { name: string; city: string | null; n: number; slug: string }) => ({ label: `${x.name}${x.city ? " · " + x.city : ""}`, n: x.n, href: `/chef/${x.slug}` }))}
          loading={topsQ.isLoading}
        />
        <TopList title={`Chef con più richieste (${days}g)`}
          rows={(t?.most_requested ?? []).map((x: { name: string; city: string | null; n: number; slug: string }) => ({ label: `${x.name}${x.city ? " · " + x.city : ""}`, n: x.n, href: `/chef/${x.slug}` }))}
          loading={topsQ.isLoading}
        />
      </div>
    </div>
  );
}

function Block({ title, desc, children }: { title: string; desc?: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4">
        <h3 className="font-display text-lg">{title}</h3>
        {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

function Kpi({ label, value, desc, loading }: { label: string; value: number | string | null | undefined; desc: string; loading: boolean }) {
  const display = loading ? "…" : value === null || value === undefined ? "—" : String(value);
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl">{display}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">{desc}</p>
    </div>
  );
}

function TopList({ title, rows, loading }: {
  title: string;
  rows: Array<{ label: string; n: number; href?: string }>;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="font-display text-lg mb-3">{title}</h3>
      {loading ? (
        <p className="text-sm text-muted-foreground">Caricamento…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessun dato nel periodo.</p>
      ) : (
        <ol className="space-y-1.5 text-sm">
          {rows.map((r, i) => (
            <li key={`${r.label}-${i}`} className="flex items-center justify-between gap-3">
              <span className="truncate">
                <span className="text-muted-foreground tabular-nums mr-2">{i + 1}.</span>
                {r.href ? <a href={r.href} className="hover:underline">{r.label}</a> : r.label}
              </span>
              <span className="tabular-nums text-muted-foreground">{r.n}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 👑 CEO COMMAND CENTER
// Vista executive per Founder/CEO. Riusa adminOverview + adminCeoExtras in
// parallelo. Tutte le card sono cliccabili e portano alla tab pertinente.
// ─────────────────────────────────────────────────────────────────────────────
function CeoTab({ onNavigate }: { onNavigate: (tab: string) => void }) {
  // Riusa adminOverview + adminCeoExtras (estesi nello Sprint 24.4 con i KPI mancanti).
  // Nessuna nuova tabella, nessuna nuova server fn.
  const overviewFn = useServerFn(adminOverview);
  const extrasFn = useServerFn(adminCeoExtras);
  const ovQ = useQuery({ queryKey: ["admin-overview"], queryFn: () => overviewFn(), staleTime: 30_000 });
  const exQ = useQuery({ queryKey: ["admin-ceo-extras"], queryFn: () => extrasFn(), staleTime: 30_000 });

  const k = ovQ.data?.kpis;
  const a = ovQ.data?.attention;
  const recent = ovQ.data?.recent ?? [];
  const ex = exQ.data;
  const isLoading = ovQ.isLoading || exQ.isLoading;

  const founderTaken = ex?.promo?.taken ?? k?.founders ?? 0;
  const founderMax = ex?.promo?.max_chefs ?? 100;
  const founderRemaining = ex?.promo?.remaining ?? Math.max(0, founderMax - founderTaken);
  const founderPct = Math.min(100, Math.round((founderTaken / Math.max(1, founderMax)) * 100));

  // Premium Free = totale − (silver + gold + founder con premium attivo già contati). Conservativo.
  const premiumActive = k?.premium_active ?? 0;
  const premiumFree = Math.max(0, (k?.chefs_total ?? 0) - premiumActive);

  // Obiettivi Founder (target arbitrari early-stage, non inventano dati: usano valori reali).
  const goals = [
    { label: "10 chef verificati", current: k?.verified ?? 0, target: 10 },
    { label: "50 richieste totali", current: ex?.requests_month ?? 0, target: 50 },
    { label: "10 recensioni", current: ex?.reviews_approved ?? 0, target: 10 },
  ];

  const attention = [
    a && a.pending_verifications > 0 && { label: "Verifiche da approvare", value: a.pending_verifications, hint: "Approva o rifiuta le richieste", tab: "verif", tone: "emerald" as const },
    a && a.premium_expiring_7d > 0 && { label: "Premium in scadenza (7 gg)", value: a.premium_expiring_7d, hint: "Chef con abbonamento in scadenza", tab: "chefs", tone: "amber" as const },
    ex && ex.unanswered_requests > 0 && { label: "Richieste senza risposta", value: ex.unanswered_requests, hint: "Lo chef non ha ancora risposto", tab: "overview", tone: "rose" as const },
    a && a.hidden_listings > 0 && { label: "Annunci nascosti", value: a.hidden_listings, hint: "Annunci attualmente non visibili", tab: "listings", tone: "zinc" as const },
    ex && (ex.chefs_hidden_inactivity ?? 0) > 0 && { label: "Profili nascosti per inattività", value: ex!.chefs_hidden_inactivity, hint: "Inattivi: invitati a riattivarsi", tab: "chefs", tone: "zinc" as const },
    a && a.media_today > 0 && { label: "Media caricati oggi", value: a.media_today, hint: "Nuovi caricamenti da controllare", tab: "media", tone: "blue" as const },
  ].filter(Boolean) as Array<{ label: string; value: number; hint: string; tab: string; tone: "emerald" | "amber" | "rose" | "zinc" | "blue" }>;

  const toneClasses: Record<string, string> = {
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    rose: "border-rose-500/30 bg-rose-500/5",
    zinc: "border-border bg-secondary/40",
    blue: "border-blue-500/30 bg-blue-500/5",
  };

  const quickActions: Array<{ label: string; icon: typeof Users; tab: string }> = [
    { label: "Verifiche", icon: CheckCircle2, tab: "verif" },
    { label: "Gestione Chef", icon: Users, tab: "chefs" },
    { label: "Utenti", icon: UserCog, tab: "users" },
    { label: "Annunci", icon: Megaphone, tab: "listings" },
    { label: "Media", icon: ImageIcon, tab: "media" },
    { label: "Founder & Promo", icon: Sparkles, tab: "promo" },
    { label: "Premium", icon: Crown, tab: "business" },
    { label: "Audit", icon: History, tab: "audit" },
  ];

  const dot = (s: "green" | "yellow" | "red" | "unknown") =>
    s === "green" ? "🟢" : s === "yellow" ? "🟡" : s === "red" ? "🔴" : "⚪";

  const fmt = (v: number | string | null | undefined) => isLoading ? "…" : v === null || v === undefined ? "—" : String(v);

  return (
    <div className="space-y-8">
      {/* Hero + Obiettivo Founder */}
      <div className="grid gap-4 lg:grid-cols-[1fr_minmax(0,360px)]">
        <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 via-background to-background p-6">
          <div className="flex items-center gap-2">
            <Crown className="h-6 w-6 text-gold" />
            <h2 className="font-display text-2xl">📊 CEO Dashboard</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Lo stato di ChefConnect in un colpo d'occhio. Tutto ciò che serve sapere oggi.
          </p>
        </div>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="flex items-center justify-between">
            <p className="inline-flex items-center gap-2 text-sm font-medium">
              <span aria-hidden>🎯</span> Obiettivo Founder
            </p>
            <span className="text-xs text-muted-foreground">{founderRemaining} disponibili</span>
          </div>
          <p className="mt-3 font-display text-2xl">{fmt(founderTaken)} <span className="text-base text-muted-foreground">/ {founderMax}</span></p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-amber-500/10">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${founderPct}%` }} />
          </div>
          <ul className="mt-4 space-y-2 text-xs">
            {goals.map((g) => {
              const done = g.current >= g.target;
              const pct = Math.min(100, Math.round((g.current / g.target) * 100));
              return (
                <li key={g.label}>
                  <div className="flex items-center justify-between">
                    <span className={done ? "font-medium text-emerald-700 dark:text-emerald-400" : ""}>{g.label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {done ? "✓ completato" : `${g.current} / ${g.target}`}
                    </span>
                  </div>
                  <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div className={`h-full ${done ? "bg-emerald-500" : "bg-amber-500/70"}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Alert verifiche pendenti — sempre in cima, un click → tab Verifiche. */}
      {(k?.pending_verifications ?? 0) > 0 && (
        <button
          type="button"
          onClick={() => onNavigate("verif")}
          className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 text-left transition hover:border-emerald-500/70 hover:bg-emerald-500/15"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl" aria-hidden>🔔</span>
            <div>
              <p className="font-display text-lg">
                Hai <strong>{k?.pending_verifications}</strong> {k?.pending_verifications === 1 ? "verifica" : "verifiche"} da approvare
              </p>
              <p className="text-xs text-muted-foreground">Apri la tab Verifiche per approvare o rifiutare le richieste.</p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-emerald-600 transition group-hover:translate-x-1 dark:text-emerald-400" />
        </button>
      )}

      {/* KPI Blocks — 1 col mobile, 2 col tablet, 4 col desktop */}
      <CeoBlock title="👥 Utenti" tab="users" onNavigate={onNavigate}>
        <CeoKpi label="Registrati" value={fmt(ex?.users_total)} />
        <CeoKpi label="Nuovi oggi" value={fmt(ex?.users_new_today)} />
        <CeoKpi label="Ultimi 7 giorni" value={fmt(ex?.users_new_7d)} />
        <CeoKpi label="Ultimi 30 giorni" value={fmt(ex?.users_new_30d)} />
      </CeoBlock>

      <CeoBlock title="👨‍🍳 Chef" tab="chefs" onNavigate={onNavigate}>
        <CeoKpi label="Registrati" value={fmt(k?.chefs_total)} />
        <CeoKpi label="Verificati" value={fmt(k?.verified)} />
        <CeoKpi label="In attesa verifica" value={fmt(k?.pending_verifications)} />
        <CeoKpi label="Nascosti per inattività" value={fmt(ex?.chefs_hidden_inactivity)} />
      </CeoBlock>

      <CeoBlock title="📢 Annunci" tab="listings" onNavigate={onNavigate}>
        <CeoKpi label="Attivi" value={fmt(ex?.listings_active)} />
        <CeoKpi label="Nascosti" value={fmt(ex?.listings_inactive)} />
        <CeoKpi label="Creati ultimi 7 gg" value={fmt(ex?.listings_new_7d)} />
        <CeoKpi label="—" value="—" muted />
      </CeoBlock>

      <CeoBlock title="📨 Richieste">
        <CeoKpi label="Oggi" value={fmt(k?.requests_today)} />
        <CeoKpi label="Questa settimana" value={fmt(ex?.requests_week)} />
        <CeoKpi label="Questo mese" value={fmt(ex?.requests_month)} />
        <CeoKpi label="Aperte / Concluse" value={fmt(ex ? `${ex.requests_open} / ${ex.requests_closed}` : null)} />
      </CeoBlock>

      {/* Recensioni — mostrate solo se realmente presenti. */}
      {(ex?.reviews_approved ?? 0) > 0 && (
        <CeoBlock title="⭐ Recensioni">
          <CeoKpi label="Pubblicate" value={fmt(ex?.reviews_approved)} />
          <CeoKpi label="Media stelle" value={ex?.reviews_avg != null ? `${ex.reviews_avg.toFixed(2)} ★` : "—"} />
          <CeoKpi label="—" value="—" muted />
          <CeoKpi label="—" value="—" muted />
        </CeoBlock>
      )}

      <CeoBlock title="💳 Premium" tab="business" onNavigate={onNavigate}>
        <CeoKpi label="Free" value={fmt(premiumFree)} />
        <CeoKpi label="Silver" value={fmt(ex?.premium_silver)} />
        <CeoKpi label="Gold" value={fmt(ex?.premium_gold)} />
        <CeoKpi label="Premium attivi totali" value={fmt(premiumActive)} emphasis />
      </CeoBlock>

      <CeoBlock title="🎁 Founder" tab="promo" onNavigate={onNavigate}>
        <CeoKpi label="Assegnati" value={fmt(founderTaken)} />
        <CeoKpi label="Rimanenti" value={fmt(founderRemaining)} />
        <CeoKpi label="Promo attiva" value={ex?.promo ? (ex.promo.active ? "Sì" : "No") : "—"} />
        <CeoKpi label="Durata bonus" value={ex?.promo ? `${ex.promo.duration_days} gg` : "—"} muted />
      </CeoBlock>

      <CeoBlock title="🌍 Piattaforma">
        <CeoKpi label="Città coperte" value={fmt(ex?.cities_covered)} />
        <CeoKpi label="Regioni coperte" value={fmt(ex?.regions_covered)} />
        <CeoKpi label="Chef online" value={fmt(ex ? Math.max(0, (k?.chefs_total ?? 0) - ex.chefs_invisible) : null)} />
        <CeoKpi label="Chef nascosti" value={fmt(ex?.chefs_invisible)} />
      </CeoBlock>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Centro attenzione */}
        <section>
          <h3 className="mb-3 inline-flex items-center gap-2 font-display text-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" /> Centro attenzione
          </h3>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento…</p>
          ) : attention.length === 0 ? (
            <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm">
              ✨ Tutto in regola. Nessuna azione richiesta.
            </p>
          ) : (
            <ul className="space-y-2">
              {attention.map((it) => (
                <li key={it.label}>
                  <button
                    type="button"
                    onClick={() => onNavigate(it.tab)}
                    className={`group flex w-full items-center justify-between gap-3 rounded-xl border p-4 text-left transition hover:border-gold ${toneClasses[it.tone]}`}
                  >
                    <div>
                      <p className="font-medium">{it.label}</p>
                      <p className="text-xs text-muted-foreground">{it.hint}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-2xl">{it.value}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-gold" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Performance / Salute piattaforma */}
        <section>
          <h3 className="mb-3 inline-flex items-center gap-2 font-display text-lg">
            <Activity className="h-5 w-5 text-gold" /> Performance
          </h3>
          <ul className="divide-y divide-border rounded-xl border border-border bg-card">
            <li className="flex items-center justify-between gap-3 p-4 text-sm">
              <span className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> Email (24h)</span>
              <span className="text-xs text-muted-foreground">
                {isLoading ? "…" : `${dot(ex?.email_health ?? "unknown")} ${ex?.email_sent_24h ?? 0} inviate · ${ex?.email_failed_24h ?? 0} fallite`}
              </span>
            </li>
            <li className="flex items-center justify-between gap-3 p-4 text-sm">
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> Ultima email inviata</span>
              <span className="text-xs text-muted-foreground">
                {isLoading ? "…" : ex?.last_email_at ? new Date(ex.last_email_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
              </span>
            </li>
            <li className="flex items-center justify-between gap-3 p-4 text-sm">
              <span className="inline-flex items-center gap-2"><CreditCard className="h-4 w-4 text-muted-foreground" /> Stripe</span>
              <span className="text-xs text-muted-foreground">
                {isLoading ? "…" : ex?.stripe_configured ? "🟢 Configurato" : "🔴 Non configurato"}
              </span>
            </li>
            <li className="flex items-center justify-between gap-3 p-4 text-sm">
              <span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /> Ultimo cron inattività</span>
              <span className="text-xs text-muted-foreground" title="Non ancora tracciato">Non disponibile</span>
            </li>
            <li className="flex items-center justify-between gap-3 p-4 text-sm">
              <span className="inline-flex items-center gap-2"><Inbox className="h-4 w-4 text-muted-foreground" /> Queue email (in coda)</span>
              <span className="text-xs text-muted-foreground" title="pgmq non esposto via Data API">Non disponibile</span>
            </li>
          </ul>
        </section>
      </div>

      {/* Azioni rapide */}
      <section>
        <h3 className="mb-3 inline-flex items-center gap-2 font-display text-lg">
          <ArrowRight className="h-5 w-5 text-gold" /> Azioni rapide
        </h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {quickActions.map((qa) => (
            <Button key={qa.label} variant="outline" onClick={() => onNavigate(qa.tab)} className="justify-start hover:border-gold">
              <qa.icon className="mr-1.5 h-4 w-4" /> {qa.label}
            </Button>
          ))}
        </div>
      </section>

      {/* Attività recenti */}
      <section>
        <h3 className="mb-3 inline-flex items-center gap-2 font-display text-lg">
          <Clock className="h-5 w-5 text-gold" /> Attività recenti
        </h3>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Caricamento…</p>
        ) : recent.length === 0 ? (
          <p className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">Nessuna attività recente.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border bg-card">
            {recent.slice(0, 20).map((ev, i) => (
              <li key={`${ev.kind}-${i}`} className="flex items-start justify-between gap-3 p-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{ev.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{ev.subtitle}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                  <span>{new Date(ev.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                  {ev.href && (
                    <a href={ev.href} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CeoBlock({
  title, tab, onNavigate, children,
}: {
  title: string;
  tab?: string;
  onNavigate?: (tab: string) => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg">{title}</h3>
        {tab && onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate(tab)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-gold"
          >
            Apri <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

function CeoKpi({ label, value, emphasis, muted }: { label: string; value: string; emphasis?: boolean; muted?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${emphasis ? "border-gold/40 bg-gold/5" : "border-border bg-background/40"} ${muted ? "opacity-60" : ""}`}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display ${emphasis ? "text-3xl text-gold" : "text-2xl"}`}>{value}</p>
    </div>
  );
}

function OverviewTab({ onNavigate }: { onNavigate: (tab: string) => void }) {
  const overviewFn = useServerFn(adminOverview);
  const q = useQuery({ queryKey: ["admin-overview"], queryFn: () => overviewFn(), staleTime: 30_000 });
  const k = q.data?.kpis;
  const a = q.data?.attention;
  const recent = q.data?.recent ?? [];

  const kpiItems = [
    { label: "Chef registrati", value: k?.chefs_total, icon: Users, color: "text-foreground" },
    { label: "Verificati", value: k?.verified, icon: BadgeCheck, color: "text-blue-600 dark:text-blue-400" },
    { label: "Founder", value: k?.founders, icon: Award, color: "text-amber-600 dark:text-amber-400" },
    { label: "Premium attivi", value: k?.premium_active, icon: Crown, color: "text-gold" },
    { label: "Verifiche pendenti", value: k?.pending_verifications, icon: Inbox, color: "text-emerald-600 dark:text-emerald-400" },
    { label: "Nuovi chef oggi", value: k?.new_chefs_today, icon: TrendingUp, color: "text-violet-600 dark:text-violet-400" },
    { label: "Richieste oggi", value: k?.requests_today, icon: MessageSquare, color: "text-rose-600 dark:text-rose-400" },
  ];

  const attentionItems = [
    a && a.pending_verifications > 0 && { label: "Verifiche pendenti", value: a.pending_verifications, hint: "Approva o rifiuta le richieste", tab: "verif", tone: "emerald" as const },
    a && a.premium_expiring_7d > 0 && { label: "Premium in scadenza (7 giorni)", value: a.premium_expiring_7d, hint: "Chef con abbonamento in scadenza", tab: "chefs", tone: "amber" as const },
    a && a.hidden_listings > 0 && { label: "Annunci nascosti", value: a.hidden_listings, hint: "Annunci attualmente non visibili", tab: "listings", tone: "zinc" as const },
    a && a.media_today > 0 && { label: "Nuovi media oggi", value: a.media_today, hint: "Da controllare in moderazione", tab: "media", tone: "blue" as const },
  ].filter(Boolean) as Array<{ label: string; value: number; hint: string; tab: string; tone: "emerald" | "amber" | "zinc" | "blue" }>;

  const toneClasses = {
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    zinc: "border-border bg-secondary/40",
    blue: "border-blue-500/30 bg-blue-500/5",
  };

  return (
    <div className="space-y-8">
      {/* KPI grid — big, readable */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {kpiItems.map((it) => (
          <div key={it.label} className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <it.icon className={`h-4 w-4 ${it.color}`} /> {it.label}
            </div>
            <p className="mt-2 font-display text-3xl">{q.isLoading ? "…" : (it.value ?? 0)}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Requires attention */}
        <section>
          <h2 className="mb-3 inline-flex items-center gap-2 font-display text-xl">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" /> Richiede attenzione
          </h2>
          {q.isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento…</p>
          ) : attentionItems.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
              Tutto in regola. Nessuna azione richiesta.
            </p>
          ) : (
            <ul className="space-y-2">
              {attentionItems.map((it) => (
                <li key={it.label}>
                  <button
                    type="button"
                    onClick={() => onNavigate(it.tab)}
                    className={`group flex w-full items-center justify-between gap-3 rounded-xl border p-4 text-left transition hover:border-gold ${toneClasses[it.tone]}`}
                  >
                    <div>
                      <p className="font-medium">{it.label}</p>
                      <p className="text-xs text-muted-foreground">{it.hint}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-2xl">{it.value}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-gold" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {a && a.premium_expiring_sample.length > 0 && (
            <div className="mt-4 rounded-xl border border-border bg-card p-4">
              <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">Premium in scadenza — prossimi 5</p>
              <ul className="divide-y divide-border text-sm">
                {a.premium_expiring_sample.map((c) => (
                  <li key={c.user_id} className="flex items-center justify-between py-2">
                    <a href={`/chef/${c.slug}`} target="_blank" rel="noopener noreferrer" className="font-medium hover:text-gold hover:underline">
                      {c.display_name}
                    </a>
                    <span className="text-xs text-muted-foreground">
                      {new Date(c.premium_until).toLocaleDateString("it-IT")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section>
          <h2 className="mb-3 inline-flex items-center gap-2 font-display text-xl">
            <Clock className="h-5 w-5 text-gold" /> Attività recenti
          </h2>
          {q.isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento…</p>
          ) : recent.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">Nessuna attività recente.</p>
          ) : (
            <ul className="divide-y divide-border rounded-xl border border-border bg-card">
              {recent.map((ev, i) => (
                <li key={`${ev.kind}-${i}`} className="flex items-start justify-between gap-3 p-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{ev.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{ev.subtitle}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                    <span>{new Date(ev.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    {ev.href && (
                      <a href={ev.href} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function VerificationsTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListVerifications);
  const docsFn = useServerFn(adminGetVerificationDocs);
  const reviewFn = useServerFn(adminReviewVerification);

  const [openId, setOpenId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  const listQ = useQuery({ queryKey: ["admin-verif"], queryFn: () => listFn() });
  const docsQ = useQuery({
    enabled: !!openId,
    queryKey: ["admin-verif-docs", openId],
    queryFn: () => docsFn({ data: { verification_id: openId! } }),
  });

  const reviewMut = useMutation({
    mutationFn: (v: { verification_id: string; chef_id: string; decision: "approved" | "rejected" }) =>
      reviewFn({ data: { ...v, notes: notes || undefined } }),
    onSuccess: () => {
      toast.success("Revisione completata");
      setOpenId(null); setNotes("");
      qc.invalidateQueries({ queryKey: ["admin-verif"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = listQ.data ?? [];
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-sm text-muted-foreground">Nessuna richiesta.</p>}
      {items.map((v) => {
        const chef = (v as unknown as { chefs: { display_name: string; city: string | null; region: string | null; slug: string } }).chefs;
        const isOpen = openId === v.id;
        return (
          <div key={v.id} className="rounded-xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg">{chef.display_name}</p>
                <p className="text-xs text-muted-foreground">{chef.city ?? "—"} · {chef.region ?? "—"} · inviata {new Date(v.submitted_at).toLocaleDateString("it-IT")}</p>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${
                v.status === "approved" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" :
                v.status === "rejected" ? "bg-red-500/15 text-red-700 dark:text-red-300" :
                "bg-amber-500/15 text-amber-700 dark:text-amber-300"
              }`}>{v.status}</span>
              <Button variant="outline" size="sm" onClick={() => setOpenId(isOpen ? null : v.id)}>
                <Eye className="mr-1.5 h-4 w-4" /> {isOpen ? "Chiudi" : "Apri"}
              </Button>
            </div>
            {isOpen && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                {docsQ.isLoading && <p className="text-sm text-muted-foreground">Carico documenti…</p>}
                <ul className="space-y-2">
                  {(docsQ.data ?? []).map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3 text-sm">
                      <span><strong>{d.kind}</strong></span>
                      {d.signed_url ? (
                        <a href={d.signed_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-gold hover:underline">
                          Apri documento →
                        </a>
                      ) : <span className="text-xs text-muted-foreground">URL non disponibile</span>}
                    </li>
                  ))}
                </ul>
                {v.status === "pending" && (
                  <>
                    <Textarea rows={2} placeholder="Note (mostrate al chef)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => reviewMut.mutate({ verification_id: v.id, chef_id: v.chef_id, decision: "approved" })}
                        disabled={reviewMut.isPending}
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approva
                      </Button>
                      <Button
                        onClick={() => reviewMut.mutate({ verification_id: v.id, chef_id: v.chef_id, decision: "rejected" })}
                        disabled={reviewMut.isPending}
                        variant="destructive"
                      >
                        <XCircle className="mr-1.5 h-4 w-4" /> Rifiuta
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type ChefFilters = {
  search: string;
  city: string;
  premium?: boolean;
  founder?: boolean;
  verified?: boolean;
  visible?: boolean;
  pending?: boolean;
};

function ChefsTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListChefs);
  const toggleFn = useServerFn(adminToggleChefFlag);
  const tierFn = useServerFn(adminSetPremiumTier);

  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState<ChefFilters>({ search: "", city: "" });
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Debounce input → filters.search
  useEffect(() => {
    const t = setTimeout(() => { setFilters((f) => ({ ...f, search: searchInput.trim() })); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["admin-chefs", filters, page],
    queryFn: () => listFn({
      data: {
        search: filters.search || undefined,
        city: filters.city || undefined,
        flt_premium: filters.premium,
        flt_founder: filters.founder,
        flt_verified: filters.verified,
        flt_visible: filters.visible,
        flt_pending_verif: filters.pending,
        page,
        page_size: pageSize,
      },
    }),
  });

  const mut = useMutation({
    mutationFn: (v: { user_id: string; verified_badge?: boolean; sponsored_until?: string | null; is_visible?: boolean }) =>
      toggleFn({ data: v }),
    onSuccess: () => { toast.success("Aggiornato"); qc.invalidateQueries({ queryKey: ["admin-chefs"] }); qc.invalidateQueries({ queryKey: ["admin-kpis"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const tierMut = useMutation({
    mutationFn: (v: { user_id: string; tier: "free" | "silver" | "gold"; duration_days?: number }) =>
      tierFn({ data: v }),
    onSuccess: () => {
      toast.success("Piano aggiornato");
      qc.invalidateQueries({ queryKey: ["admin-chefs"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
      qc.invalidateQueries({ queryKey: ["admin-ceo-extras"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [tierConfirm, setTierConfirm] = useState<
    | { user_id: string; name: string; tier: "free" | "silver" | "gold" }
    | null
  >(null);

  const data = query.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleFlag = (key: keyof ChefFilters) => {
    setFilters((f) => ({ ...f, [key]: f[key] === true ? undefined : true }));
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Search + filters */}
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Cerca nome, email, città, slug…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <Input
            className="md:max-w-[200px]"
            placeholder="Filtra per città"
            value={filters.city}
            onChange={(e) => { setFilters((f) => ({ ...f, city: e.target.value })); setPage(1); }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={filters.premium === true} onClick={() => toggleFlag("premium")} label="Premium" />
          <FilterChip active={filters.founder === true} onClick={() => toggleFlag("founder")} label="Founder" />
          <FilterChip active={filters.verified === true} onClick={() => toggleFlag("verified")} label="Verificato" />
          <FilterChip active={filters.visible === true} onClick={() => toggleFlag("visible")} label="Visibile" />
          <FilterChip active={filters.pending === true} onClick={() => toggleFlag("pending")} label="In attesa di verifica" />
          {(filters.search || filters.city || filters.premium || filters.founder || filters.verified || filters.visible || filters.pending) && (
            <Button size="sm" variant="ghost" onClick={() => { setSearchInput(""); setFilters({ search: "", city: "" }); setPage(1); }}>
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3">Chef</th>
              <th>Email</th>
              <th>Città</th>
              <th>Badge</th>
              <th>Verificato</th>
              <th>Sponsor</th>
              <th>Visibile</th>
              <th>Ultima attività</th>
              <th className="pr-3 text-right">Profilo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {query.isLoading && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Caricamento…</td></tr>
            )}
            {!query.isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center">
                <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium">Nessun chef corrisponde ai filtri</p>
                <p className="mt-1 text-xs text-muted-foreground">Prova a cambiare ricerca o rimuovere i filtri attivi.</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => { setSearchInput(""); setFilters({ search: "", city: "" }); setPage(1); }}>Reset filtri</Button>
              </td></tr>
            )}
            {rows.map((c) => {
              const sponsored = c.sponsored_until && new Date(c.sponsored_until) > new Date();
              const premium = c.premium_until && new Date(c.premium_until) > new Date();
              const tier = (premium ? ((c as { premium_tier?: string | null }).premium_tier ?? "free") : "free") as "free" | "silver" | "gold";
              const tierLabel = tier === "gold" ? "Gold" : tier === "silver" ? "Silver" : "Free";
              const tierClass = tier === "gold"
                ? "bg-gold/20 text-gold border-gold/40"
                : tier === "silver"
                  ? "bg-slate-300/30 text-slate-900 dark:text-slate-100 border-slate-400/50"
                  : "bg-muted text-muted-foreground border-border";
              return (
                <tr key={c.user_id} className="align-middle">
                  <td className="p-3">
                    <strong>{c.display_name}</strong>
                    <div className="text-xs text-muted-foreground">{c.full_name ?? c.slug}</div>
                  </td>
                  <td className="text-xs text-muted-foreground">{c.email ?? "—"}</td>
                  <td>{c.city ?? "—"}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase transition hover:opacity-80 ${tierClass}`}
                            title="Cambia piano Premium"
                          >
                            {tierLabel}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Piano Premium</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={tier === "free"}
                            onClick={() => setTierConfirm({ user_id: c.user_id, name: c.display_name, tier: "free" })}
                          >
                            Imposta Free
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={tier === "silver"}
                            onClick={() => setTierConfirm({ user_id: c.user_id, name: c.display_name, tier: "silver" })}
                          >
                            Promuovi a Silver (30 gg)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={tier === "gold"}
                            onClick={() => setTierConfirm({ user_id: c.user_id, name: c.display_name, tier: "gold" })}
                          >
                            Promuovi a Gold (30 gg)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {c.founder_badge && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300">Founder</span>}
                    </div>
                  </td>
                  <td><Switch checked={c.verified_badge} onCheckedChange={(v) => mut.mutate({ user_id: c.user_id, verified_badge: v })} /></td>
                  <td>
                    <Button size="sm" variant={sponsored ? "default" : "outline"}
                      onClick={() => {
                        const until = sponsored ? null : new Date(Date.now() + 30 * 86400000).toISOString();
                        mut.mutate({ user_id: c.user_id, sponsored_until: until });
                      }}
                    >{sponsored ? "Disattiva" : "+30 gg"}</Button>
                  </td>
                  <td><Switch checked={c.is_visible} onCheckedChange={(v) => mut.mutate({ user_id: c.user_id, is_visible: v })} /></td>
                  <td className="text-xs">
                    {c.last_active_at
                      ? new Date(c.last_active_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" })
                      : "—"}
                    {c.hidden_for_inactivity ? (
                      <span className="ml-1 inline-flex items-center rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700 dark:text-amber-300">
                        nascosto per inattività
                      </span>
                    ) : (
                      <span className="ml-1 inline-flex items-center rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                        attivo
                      </span>
                    )}
                  </td>
                  <td className="pr-3 text-right">
                    <a
                      href={`/chef/${c.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-gold hover:underline"
                    >
                      Apri <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <p className="text-muted-foreground">
          <strong>{total}</strong> chef · pagina <strong>{page}</strong> di <strong>{totalPages}</strong>
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1 || query.isLoading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" /> Precedente
          </Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages || query.isLoading} onClick={() => setPage((p) => p + 1)}>
            Successiva <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AlertDialog open={!!tierConfirm} onOpenChange={(o) => !o && setTierConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tierConfirm?.tier === "free"
                ? "Rimuovere Premium?"
                : tierConfirm?.tier === "gold"
                  ? "Promuovere a Premium Gold?"
                  : "Promuovere a Premium Silver?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{tierConfirm?.name}</strong>
              {tierConfirm?.tier === "free"
                ? " — il piano tornerà Free e il Premium verrà rimosso."
                : ` — il piano sarà attivo per 30 giorni. Il badge Founder, se presente, non viene modificato.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!tierConfirm) return;
                tierMut.mutate({ user_id: tierConfirm.user_id, tier: tierConfirm.tier });
                setTierConfirm(null);
              }}
            >
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
        active
          ? "border-gold bg-gold text-gold-foreground"
          : "border-border bg-background text-foreground hover:bg-secondary"
      }`}
    >
      {label}
    </button>
  );
}

function PromoTab() {
  const qc = useQueryClient();
  const getFn = useServerFn(adminGetPromoSettings);
  const updFn = useServerFn(adminUpdatePromoSettings);
  const q = useQuery({ queryKey: ["promo-settings"], queryFn: () => getFn() });
  const [form, setForm] = useState<{ active: boolean; max_chefs: number; duration_days: number } | null>(null);
  const current = form ?? (q.data ? { active: q.data.active, max_chefs: q.data.max_chefs, duration_days: q.data.duration_days } : null);

  const mut = useMutation({
    mutationFn: () => updFn({ data: current! }),
    onSuccess: () => { toast.success("Impostazioni salvate"); qc.invalidateQueries({ queryKey: ["promo-settings"] }); qc.invalidateQueries({ queryKey: ["launch-promo"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!current) return <p className="text-sm text-muted-foreground">Caricamento…</p>;
  return (
    <div className="max-w-lg rounded-xl border border-border bg-card p-6">
      <div className="inline-flex items-center gap-2"><SettingsIcon className="h-5 w-5 text-gold" /><h2 className="font-display text-2xl">Offerta di lancio</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Premium gratis + badge Founder ai primi N chef.</p>
      <div className="mt-5 space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <Label>Promo attiva</Label>
          <Switch checked={current.active} onCheckedChange={(v) => setForm({ ...current, active: v })} />
        </div>
        <div>
          <Label>Numero massimo di chef</Label>
          <Input type="number" min={0} max={100000} value={current.max_chefs}
            onChange={(e) => setForm({ ...current, max_chefs: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Durata Premium (giorni)</Label>
          <Input type="number" min={1} max={3650} value={current.duration_days}
            onChange={(e) => setForm({ ...current, duration_days: Number(e.target.value) })} />
        </div>
        <Button onClick={() => mut.mutate()} disabled={mut.isPending} className="bg-gold text-gold-foreground hover:bg-gold/90">
          {mut.isPending ? "Salvo…" : "Salva"}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared confirm dialog — every destructive admin action goes through this hook.
// Centralizing here means a future `admin_actions` table can be wired in once.
// ─────────────────────────────────────────────────────────────────────────────
function useConfirmDialog() {
  const [state, setState] = useState<{
    title: string; description: ReactNode; confirmLabel: string; destructive?: boolean;
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  const ask = (opts: NonNullable<typeof state>) => setState(opts);
  const node = (
    <AlertDialog open={!!state} onOpenChange={(o) => !o && setState(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{state?.title}</AlertDialogTitle>
          <AlertDialogDescription>{state?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction
            className={state?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            onClick={async () => { await state?.onConfirm(); setState(null); }}
          >
            {state?.confirmLabel ?? "Conferma"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { ask, node };
}

// ─────────────────────────────────────────────────────────────────────────────
// ANNUNCI (chef_listings)
// ─────────────────────────────────────────────────────────────────────────────
function ListingsTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListListings);
  const toggleFn = useServerFn(adminToggleListing);
  const delFn = useServerFn(adminDeleteListing);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const confirm = useConfirmDialog();

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["admin-listings", search, visible, page],
    queryFn: () => listFn({ data: { search: search || undefined, visible, page, page_size: pageSize } }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-listings"] });
  const toggleMut = useMutation({
    mutationFn: (v: { id: string; is_active: boolean }) => toggleFn({ data: v }),
    onSuccess: () => { toast.success("Aggiornato"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { toast.success("Annuncio eliminato"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = query.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      {confirm.node}
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca titolo, chef, città, slug…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={visible === true} onClick={() => { setVisible(visible === true ? undefined : true); setPage(1); }} label="Visibili" />
          <FilterChip active={visible === false} onClick={() => { setVisible(visible === false ? undefined : false); setPage(1); }} label="Nascosti" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3">Annuncio</th>
              <th>Chef</th>
              <th>Città</th>
              <th>Data</th>
              <th>Stato</th>
              <th className="pr-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {query.isLoading && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Caricamento…</td></tr>}
            {!query.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center">
                <Megaphone className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium">Nessun annuncio corrisponde ai filtri</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => { setSearchInput(""); setSearch(""); setVisible(undefined); setPage(1); }}>Reset filtri</Button>
              </td></tr>
            )}
            {rows.map((l) => (
              <tr key={l.id}>
                <td className="p-3">
                  <strong>{l.title}</strong>
                  {l.event_type && <div className="text-xs text-muted-foreground">{l.event_type}</div>}
                </td>
                <td>{l.chefs.display_name}</td>
                <td>{l.chefs.city ?? "—"}</td>
                <td className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString("it-IT")}</td>
                <td>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    l.is_active ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300"
                  }`}>{l.is_active ? "Visibile" : "Nascosto"}</span>
                </td>
                <td className="pr-3">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <a href={`/chef/${l.chefs.slug}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary">
                      <Eye className="h-3 w-3" /> Visualizza
                    </a>
                    {l.is_active ? (
                      <Button size="sm" variant="outline" onClick={() => confirm.ask({
                        title: "Nascondere l'annuncio?",
                        description: `“${l.title}” non sarà più visibile pubblicamente. Puoi ripristinarlo in qualsiasi momento.`,
                        confirmLabel: "Nascondi",
                        onConfirm: () => toggleMut.mutate({ id: l.id, is_active: false }),
                      })}>
                        <EyeOff className="mr-1 h-3 w-3" /> Nascondi
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => confirm.ask({
                        title: "Ripristinare l'annuncio?",
                        description: `“${l.title}” tornerà visibile pubblicamente.`,
                        confirmLabel: "Ripristina",
                        onConfirm: () => toggleMut.mutate({ id: l.id, is_active: true }),
                      })}>
                        <Eye className="mr-1 h-3 w-3" /> Ripristina
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => confirm.ask({
                      title: "Eliminare definitivamente l'annuncio?",
                      description: <>Stai per eliminare <strong>“{l.title}”</strong>. L'operazione è irreversibile.</>,
                      confirmLabel: "Elimina",
                      destructive: true,
                      onConfirm: () => delMut.mutate(l.id),
                    })}>
                      <Trash2 className="mr-1 h-3 w-3" /> Elimina
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination total={total} page={page} totalPages={totalPages} setPage={setPage} disabled={query.isLoading} label="annunci" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MEDIA (foto, video, menu pdf)
// ─────────────────────────────────────────────────────────────────────────────
function MediaTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListMedia);
  const delFn = useServerFn(adminDeleteMedia);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<"photo" | "video" | "menu_pdf" | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 24;
  const confirm = useConfirmDialog();

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["admin-media", search, kind, page],
    queryFn: () => listFn({ data: { search: search || undefined, kind, page, page_size: pageSize } }),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { toast.success("Media eliminato"); qc.invalidateQueries({ queryKey: ["admin-media"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = query.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      {confirm.node}
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca titolo, chef, città, slug…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={kind === "photo"} onClick={() => { setKind(kind === "photo" ? undefined : "photo"); setPage(1); }} label="Foto" />
          <FilterChip active={kind === "video"} onClick={() => { setKind(kind === "video" ? undefined : "video"); setPage(1); }} label="Video" />
          <FilterChip active={kind === "menu_pdf"} onClick={() => { setKind(kind === "menu_pdf" ? undefined : "menu_pdf"); setPage(1); }} label="Menù PDF" />
        </div>
      </div>

      {query.isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>}
      {!query.isLoading && rows.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-10 text-center">
          <ImageIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm font-medium">Nessun media corrisponde ai filtri</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => { setSearchInput(""); setSearch(""); setKind(undefined); setPage(1); }}>Reset filtri</Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {rows.map((m) => (
          <div key={m.id} className="overflow-hidden rounded-xl border border-border bg-card">
            <div className="relative aspect-square bg-muted">
              {m.kind === "photo" && m.preview_url && (
                <img src={m.preview_url} alt={m.title ?? ""} className="h-full w-full object-cover" loading="lazy" />
              )}
              {m.kind === "video" && (
                <a href={m.preview_url ?? "#"} target="_blank" rel="noopener noreferrer" className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <VideoIcon className="h-10 w-10" />
                </a>
              )}
              {m.kind === "menu_pdf" && (
                <a href={m.preview_url ?? "#"} target="_blank" rel="noopener noreferrer" className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <FileText className="h-10 w-10" />
                </a>
              )}
              <span className="absolute left-2 top-2 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-bold uppercase">
                {m.kind === "menu_pdf" ? "Menù" : m.kind}
              </span>
            </div>
            <div className="space-y-1 p-3 text-xs">
              <p className="font-medium">{m.chefs.display_name}</p>
              <p className="text-muted-foreground">{m.chefs.city ?? "—"} · {new Date(m.created_at).toLocaleDateString("it-IT")}</p>
              <div className="mt-2 flex gap-1.5">
                <a href={`/chef/${m.chefs.slug}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-secondary">
                  <ExternalLink className="h-3 w-3" /> Profilo
                </a>
                <Button size="sm" variant="destructive" className="h-7 px-2"
                  onClick={() => confirm.ask({
                    title: "Eliminare questo media?",
                    description: <>Il file verrà rimosso dallo storage e dal profilo di <strong>{m.chefs.display_name}</strong>. Operazione irreversibile.</>,
                    confirmLabel: "Elimina",
                    destructive: true,
                    onConfirm: () => delMut.mutate(m.id),
                  })}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Pagination total={total} page={page} totalPages={totalPages} setPage={setPage} disabled={query.isLoading} label="media" />
    </div>
  );
}

function Pagination({ total, page, totalPages, setPage, disabled, label }: {
  total: number; page: number; totalPages: number; setPage: (n: number) => void; disabled: boolean; label: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <p className="text-muted-foreground"><strong>{total}</strong> {label} · pagina <strong>{page}</strong> di <strong>{totalPages}</strong></p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1 || disabled} onClick={() => setPage(Math.max(1, page - 1))}>
          <ChevronLeft className="h-4 w-4" /> Precedente
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages || disabled} onClick={() => setPage(page + 1)}>
          Successiva <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// 👥 USERS TAB (Sprint 18.3)
// ─────────────────────────────────────────────────────────────────────────────
type UserRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  roles: string[];
  suspended_at: string | null;
  banned_until: string | null;
  created_at: string;
  last_sign_in_at: string | null;
};

function UsersTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListUsers);
  const suspendFn = useServerFn(adminSuspendUser);
  const reactivateFn = useServerFn(adminReactivateUser);
  const rolesFn = useServerFn(adminUpdateUserRoles);
  const deleteFn = useServerFn(adminDeleteUser);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFlt, setRoleFlt] = useState<"client" | "chef" | "admin" | undefined>(undefined);
  const [suspFlt, setSuspFlt] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [editingRoles, setEditingRoles] = useState<UserRow | null>(null);
  const [confirming, setConfirming] = useState<
    | { type: "suspend" | "reactivate" | "delete"; user: UserRow }
    | null
  >(null);
  const [deleteText, setDeleteText] = useState("");

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const q = useQuery({
    queryKey: ["admin-users", search, roleFlt, suspFlt, page],
    queryFn: () => listFn({ data: { search: search || undefined, flt_role: roleFlt, flt_suspended: suspFlt, page, page_size: 25 } }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin-audit"] });
    qc.invalidateQueries({ queryKey: ["admin-overview"] });
  };

  const suspendMut = useMutation({
    mutationFn: (uid: string) => suspendFn({ data: { user_id: uid } }),
    onSuccess: () => { toast.success("Utente sospeso"); invalidate(); setConfirming(null); },
    onError: (e: Error) => toast.error(e.message),
  });
  const reactivateMut = useMutation({
    mutationFn: (uid: string) => reactivateFn({ data: { user_id: uid } }),
    onSuccess: () => { toast.success("Utente riattivato"); invalidate(); setConfirming(null); },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: (uid: string) => deleteFn({ data: { user_id: uid, confirm: "DELETE" } }),
    onSuccess: () => { toast.success("Utente eliminato"); invalidate(); setConfirming(null); setDeleteText(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  const rolesMut = useMutation({
    mutationFn: (v: { user_id: string; roles: Array<"client" | "chef" | "admin"> }) =>
      rolesFn({ data: v }),
    onSuccess: () => { toast.success("Ruoli aggiornati"); invalidate(); setEditingRoles(null); },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = (q.data?.rows ?? []) as UserRow[];
  const total = q.data?.total ?? 0;
  const pageSize = q.data?.page_size ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const hasFilters = !!(search || roleFlt || suspFlt !== undefined);
  const resetFilters = () => { setSearchInput(""); setSearch(""); setRoleFlt(undefined); setSuspFlt(undefined); setPage(1); };

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cerca nome o email…"
              className="pl-9"
            />
          </div>
          <p className="text-xs text-muted-foreground">{total} utenti</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={!roleFlt && suspFlt === undefined} onClick={resetFilters} label="Tutti" />
          <FilterChip active={roleFlt === "chef"} onClick={() => { setRoleFlt(roleFlt === "chef" ? undefined : "chef"); setPage(1); }} label="Chef" />
          <FilterChip active={roleFlt === "client"} onClick={() => { setRoleFlt(roleFlt === "client" ? undefined : "client"); setPage(1); }} label="Clienti" />
          <FilterChip active={roleFlt === "admin"} onClick={() => { setRoleFlt(roleFlt === "admin" ? undefined : "admin"); setPage(1); }} label="Admin" />
          <FilterChip active={suspFlt === true} onClick={() => { setSuspFlt(suspFlt === true ? undefined : true); setPage(1); }} label="Sospesi" />
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={resetFilters}>Reset</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Utente</th>
              <th className="px-3 py-2">Ruoli</th>
              <th className="px-3 py-2">Stato</th>
              <th className="px-3 py-2">Registrato</th>
              <th className="px-3 py-2">Ultimo accesso</th>
              <th className="px-3 py-2 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Caricamento…</td></tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center">
                <Inbox className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium">Nessun utente corrisponde ai filtri</p>
                <p className="mt-1 text-xs text-muted-foreground">Prova a cambiare ricerca o filtri attivi.</p>
                {hasFilters && (
                  <Button size="sm" variant="outline" className="mt-3" onClick={resetFilters}>Reset filtri</Button>
                )}
              </td></tr>
            )}
            {rows.map((u) => {
              const suspended = !!u.suspended_at;
              return (
                <tr key={u.id} className="border-b border-border/60 align-top">
                  <td className="px-3 py-3">
                    <div className="font-medium">{u.full_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground break-all">{u.email}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                      {u.roles.map((r) => (
                        <span key={r} className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${
                          r === "admin" ? "bg-foreground text-background" :
                          r === "chef" ? "bg-gold/20 text-gold" : "bg-muted text-muted-foreground"
                        }`}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {suspended ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
                        <Ban className="h-3 w-3" /> Sospeso
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" /> Attivo
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(u.created_at).toLocaleDateString("it-IT")}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditingRoles(u)} title="Modifica ruoli" aria-label="Modifica ruoli">
                        <UserCog className="h-4 w-4" />
                      </Button>
                      {suspended ? (
                        <Button size="sm" variant="ghost" onClick={() => setConfirming({ type: "reactivate", user: u })} title="Riattiva utente" aria-label="Riattiva utente">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setConfirming({ type: "suspend", user: u })} title="Sospendi utente" aria-label="Sospendi utente">
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setConfirming({ type: "delete", user: u })} title="Elimina utente" aria-label="Elimina utente">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">Pagina {page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Modifica ruoli */}
      <RolesDialog
        user={editingRoles}
        onClose={() => setEditingRoles(null)}
        onSave={(roles) => editingRoles && rolesMut.mutate({ user_id: editingRoles.id, roles })}
        saving={rolesMut.isPending}
      />

      {/* AlertDialog conferma azioni */}
      <AlertDialog
        open={!!confirming}
        onOpenChange={(o) => { if (!o) { setConfirming(null); setDeleteText(""); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.type === "suspend" && "Sospendere l'utente?"}
              {confirming?.type === "reactivate" && "Riattivare l'utente?"}
              {confirming?.type === "delete" && "Eliminare definitivamente l'utente?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div>
                  <strong>{confirming?.user.full_name ?? confirming?.user.email}</strong>
                </div>
                {confirming?.type === "suspend" && (
                  <p>Login disabilitato, profilo nascosto. I dati sono conservati e l'utente può essere riattivato in qualsiasi momento.</p>
                )}
                {confirming?.type === "reactivate" && (
                  <p>L'utente potrà accedere di nuovo e il profilo tornerà visibile.</p>
                )}
                {confirming?.type === "delete" && (
                  <>
                    <p className="text-destructive font-medium">⚠️ Operazione irreversibile. Tutti i dati associati saranno eliminati.</p>
                    <p>Digita <code className="bg-muted px-1 rounded">DELETE</code> per confermare:</p>
                    <Input
                      value={deleteText}
                      onChange={(e) => setDeleteText(e.target.value)}
                      placeholder="DELETE"
                      autoFocus
                    />
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirming?.type === "delete" && deleteText !== "DELETE"}
              onClick={() => {
                if (!confirming) return;
                if (confirming.type === "suspend") suspendMut.mutate(confirming.user.id);
                else if (confirming.type === "reactivate") reactivateMut.mutate(confirming.user.id);
                else if (confirming.type === "delete" && deleteText === "DELETE")
                  deleteMut.mutate(confirming.user.id);
              }}
              className={confirming?.type === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {confirming?.type === "suspend" && "Sospendi"}
              {confirming?.type === "reactivate" && "Riattiva"}
              {confirming?.type === "delete" && "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RolesDialog({
  user, onClose, onSave, saving,
}: {
  user: UserRow | null;
  onClose: () => void;
  onSave: (roles: Array<"client" | "chef" | "admin">) => void;
  saving: boolean;
}) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  useEffect(() => { if (user) setSel(new Set(user.roles)); }, [user]);

  const toggle = (r: string) => {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  };

  return (
    <AlertDialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Modifica ruoli</AlertDialogTitle>
          <AlertDialogDescription>
            <strong>{user?.full_name ?? user?.email}</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          {(["client", "chef", "admin"] as const).map((r) => (
            <label key={r} className="flex items-center justify-between rounded-lg border border-border p-3 cursor-pointer hover:bg-accent">
              <div>
                <p className="font-medium capitalize">{r}</p>
                <p className="text-xs text-muted-foreground">
                  {r === "client" && "Utente standard, può contattare chef"}
                  {r === "chef" && "Profilo professionale, può pubblicare annunci"}
                  {r === "admin" && "⚠️ Accesso completo al pannello admin"}
                </p>
              </div>
              <Switch checked={sel.has(r)} onCheckedChange={() => toggle(r)} />
            </label>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction
            disabled={saving || sel.size === 0}
            onClick={() => onSave(Array.from(sel) as Array<"client" | "chef" | "admin">)}
          >
            {saving ? "Salvo…" : "Salva ruoli"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 🕓 AUDIT LOG TAB
// ─────────────────────────────────────────────────────────────────────────────
function AuditTab() {
  const fn = useServerFn(adminListAuditLog);
  const [page, setPage] = useState(1);
  const [since, setSince] = useState<"today" | "week" | "month" | undefined>(undefined);
  const q = useQuery({
    queryKey: ["admin-audit", since, page],
    queryFn: () => fn({ data: { page, page_size: 50, since } }),
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">{total} eventi {since ? `(${since === "today" ? "oggi" : since === "week" ? "ultimi 7 giorni" : "ultimi 30 giorni"})` : "totali"}</p>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={!since} onClick={() => { setSince(undefined); setPage(1); }} label="Tutti" />
          <FilterChip active={since === "today"} onClick={() => { setSince("today"); setPage(1); }} label="Oggi" />
          <FilterChip active={since === "week"} onClick={() => { setSince("week"); setPage(1); }} label="Ultimi 7 giorni" />
          <FilterChip active={since === "month"} onClick={() => { setSince("month"); setPage(1); }} label="Ultimi 30 giorni" />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Quando</th>
              <th className="px-3 py-2">Azione</th>
              <th className="px-3 py-2">Target</th>
              <th className="px-3 py-2">Attore</th>
              <th className="px-3 py-2">Dettagli</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Caricamento…</td></tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-10 text-center">
                <History className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium">Nessun evento nel periodo selezionato</p>
                {since && <Button size="sm" variant="outline" className="mt-3" onClick={() => { setSince(undefined); setPage(1); }}>Rimuovi filtro</Button>}
              </td></tr>
            )}
            {rows.map((r: {
              id: string; created_at: string; action: string;
              target_type: string; target_id: string | null;
              actor_id: string | null; before: unknown; after: unknown;
              actor_email?: string | null; target_email?: string | null;
            }) => (
              <tr key={r.id} className="border-b border-border/60 align-top">
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString("it-IT")}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.action}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{r.target_type}</div>
                  {r.target_email
                    ? <div className="text-muted-foreground break-all">{r.target_email}</div>
                    : r.target_id && <div className="text-muted-foreground break-all font-mono">{r.target_id.slice(0, 8)}…</div>}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground break-all">
                  {r.actor_email ?? (r.actor_id ? `${r.actor_id.slice(0, 8)}…` : "—")}
                </td>
                <td className="px-3 py-2 text-xs">
                  {(!!r.before || !!r.after) && (
                    <details>
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Vedi</summary>
                      <pre className="mt-2 max-w-md overflow-auto rounded bg-muted p-2 text-[10px]">
{JSON.stringify({ before: r.before, after: r.after }, null, 2)}
                      </pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">Pagina {page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
